import React, { Component } from "react";
import axios from "../../../../Actions/Axios";
import { Mail, MoreVertical } from "lucide-react";

class Email extends Component {
  state = {
    sessions: [],
    selectedSession: null,
    messages: [],
    loadingSessions: false,
    loadingMessages: false,
    sending: false,
    to: "",
    subject: "",
    message: "",
    showHeaderMenu: false,
    showNewConversationModal: false,
    error: null,
    success: null,
  };

  componentDidMount() {
    this.fetchSessions();
  }

  fetchSessions = async () => {
    this.setState({ loadingSessions: true, error: null });
    try {
      const res = await axios.get("/api/messaging/all-chat-sessions/");
      const allSessions = Array.isArray(res.data) ? res.data : [];
      const emailSessions = allSessions.filter((s) => (s.user_phone_number || "").includes("@"));
      this.setState({ sessions: emailSessions, loadingSessions: false });
    } catch (err) {
      this.setState({
        loadingSessions: false,
        error: err?.response?.data?.message || "Failed to fetch email sessions.",
      });
    }
  };

  fetchMessages = async (session) => {
    if (!session?.user_phone_number) return;
    this.setState({
      selectedSession: session,
      loadingMessages: true,
      error: null,
      to: session.user_phone_number,
      subject: "",
      message: "",
      success: null,
    });
    try {
      const res = await axios.get(
        `/api/messaging/chat-session/${encodeURIComponent(session.user_phone_number)}/`
      );
      const messages = Array.isArray(res.data) ? res.data : [];
      this.setState({ messages, loadingMessages: false });
    } catch (err) {
      this.setState({
        loadingMessages: false,
        error: err?.response?.data?.message || "Failed to fetch email thread.",
      });
    }
  };

  sendEmail = async (e) => {
    e.preventDefault();
    const { to, subject, message } = this.state;

    if (!to || !subject || !message) {
      this.setState({ error: 'Fields "To", "Subject", and "Message" are required.' });
      return;
    }

    this.setState({ sending: true, error: null, success: null });
    try {
      await axios.post("/api/messaging/send-email/", {
        to,
        subject,
        message,
      });

      this.setState({
        sending: false,
        success: "Email sent successfully.",
        message: "",
        showNewConversationModal: false,
        showHeaderMenu: false,
      });

      await this.fetchSessions();
      const refreshedRes = await axios.get("/api/messaging/all-chat-sessions/");
      const refreshedSessions = Array.isArray(refreshedRes.data)
        ? refreshedRes.data.filter((s) => (s.user_phone_number || "").includes("@"))
        : [];
      const matchingSession = refreshedSessions.find((session) => session.user_phone_number === to);

      if (matchingSession) {
        this.setState({ sessions: refreshedSessions });
        await this.fetchMessages(matchingSession);
      } else if (this.state.selectedSession) {
        await this.fetchMessages(this.state.selectedSession);
      }
    } catch (err) {
      this.setState({
        sending: false,
        error: err?.response?.data?.error || "Failed to send email.",
      });
    }
  };

  openNewConversationModal = () => {
    this.setState({
      showNewConversationModal: true,
      showHeaderMenu: false,
      to: "",
      error: null,
      success: null,
    });
  };

  closeNewConversationModal = () => {
    this.setState({
      showNewConversationModal: false,
      error: null,
      success: null,
    });
  };

  startConversation = async (e) => {
    e.preventDefault();
    const { to, sessions } = this.state;
    const email = (to || "").trim().toLowerCase();

    if (!email) {
      this.setState({ error: 'Field "To" is required.' });
      return;
    }

    const existingSession = sessions.find(
      (session) => (session.user_phone_number || "").toLowerCase() === email
    );

    if (existingSession) {
      this.setState({
        showNewConversationModal: false,
        error: null,
        success: null,
      });
      await this.fetchMessages(existingSession);
      return;
    }

    this.setState({
      selectedSession: {
        id: `draft-${email}`,
        user_phone_number: email,
        auto_response: false,
      },
      messages: [],
      loadingMessages: false,
      to: email,
      subject: "",
      message: "",
      showNewConversationModal: false,
      error: null,
      success: null,
    });
  };

  formatMessageTime = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const now = new Date();
    const isSameDay = date.toDateString() === now.toDateString();
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isSameDay) {
      return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }

    if (isYesterday) {
      return `Yesterday ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
    }

    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  renderSessionList() {
    const { sessions, selectedSession, loadingSessions, showHeaderMenu } = this.state;
    return (
      <div className="flex flex-col w-72 shrink-0 h-full border border-gray-200 rounded-xl bg-white overflow-hidden">
        <div className="px-4 py-4 border-b border-gray-100 flex items-start justify-between gap-2 relative">
          <div>
            <h2 className="font-SFPro font-semibold text-base text-gray-900">Email Messages</h2>
            <p className="text-xs text-gray-500 mt-1">Select a thread to continue</p>
          </div>
          <button
            type="button"
            onClick={() => this.setState((prev) => ({ showHeaderMenu: !prev.showHeaderMenu }))}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="More options"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
          {showHeaderMenu && (
            <div className="absolute right-4 top-12 z-20 min-w-52 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
              <button
                type="button"
                onClick={this.openNewConversationModal}
                className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Start new conversation
              </button>
            </div>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {loadingSessions && <p className="px-4 py-3 text-sm text-gray-500">Loading sessions...</p>}
          {!loadingSessions && sessions.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-500">No email sessions yet.</p>
          )}
          {sessions.map((session) => {
            const active = selectedSession?.id === session.id;
            return (
              <button
                key={session.id}
                onClick={() => this.fetchMessages(session)}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                  active ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
              >
                <p className="text-sm font-medium text-gray-900 truncate">{session.user_phone_number}</p>
                <p className="text-xs text-gray-500 mt-1">Auto-response: {session.auto_response ? "On" : "Off"}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  renderMessages() {
    const { selectedSession, messages, loadingMessages } = this.state;

    if (!selectedSession) {
      return (
        <div className="flex-1 bg-white flex items-center justify-center">
          <p className="text-sm text-gray-500">Select an email thread from the left panel.</p>
        </div>
      );
    }

    return (
      <div className="relative w-full h-full border border-gray-200 rounded-xl overflow-hidden flex flex-col bg-white">
        <div className="w-full h-14 bg-white border-b border-blue-100 px-4 flex justify-between items-center rounded-t-xl shrink-0">
          <div>
            <div className="font-SFPro font-semibold text-base leading-tight text-gray-900">
              {selectedSession.user_phone_number}
            </div>
            <div className="font-inter text-xs text-gray-500">Email conversation</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loadingMessages && <p className="text-sm text-gray-500">Loading thread...</p>}
          {!loadingMessages && messages.length === 0 && (
            <p className="text-sm text-gray-500">No messages in this thread.</p>
          )}
          {messages.map((msg) => {
            const outgoing = msg.message_type === "outgoing";
            return (
              <div
                key={msg.id}
                className={`flex ${outgoing ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`w-[55%] rounded-2xl px-4 py-3 ${
                    outgoing
                      ? "bg-primary-blue text-white"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {msg.subject && (
                    <p className={`text-xs font-semibold mb-1 truncate ${outgoing ? "text-blue-200" : "text-gray-500"}`}>
                      {msg.subject}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.body}
                  </p>
                  <p className={`text-[10px] mt-2 text-right ${outgoing ? "text-blue-200" : "text-gray-400"}`}>
                    {this.formatMessageTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="shrink-0 border-t border-gray-100">{this.renderComposer(true)}</div>
      </div>
    );
  }

  renderComposer = (compact = false) => {
    const { subject, message, sending, error, success } = this.state;
    return (
      <div className={`${compact ? "bg-white" : "w-full max-w-2xl bg-white border border-gray-200 rounded-xl"}`}>
        {!compact && (
          <div className="px-4 py-4 border-b border-gray-100">
            <h3 className="font-SFPro font-semibold text-base text-gray-900">Compose Email</h3>
          </div>
        )}
        <form onSubmit={this.sendEmail} className="flex flex-col px-4 py-4 gap-3">
          <input
            type="text"
            value={subject}
            onChange={(e) => this.setState({ subject: e.target.value, error: null, success: null })}
            placeholder="Subject"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:outline-none focus:ring-0 focus:border-gray-300"
          />
          <textarea
            value={message}
            onChange={(e) => this.setState({ message: e.target.value, error: null, success: null })}
            placeholder="Write your email..."
            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none outline-none focus:outline-none focus:ring-0 focus:border-gray-300 ${compact ? "h-24" : "h-56"}`}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}
          {success && <p className="text-sm text-green-600">{success}</p>}

          <button
            type="submit"
            disabled={sending}
            className={`${
              compact ? "self-end px-5" : "w-full"
            } py-2.5 rounded-md bg-primary-blue text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50`}
          >
            {sending ? "Sending..." : "Send Email"}
          </button>
        </form>
      </div>
    );
  };

  renderNewConversationModal() {
    const { showNewConversationModal, to, error } = this.state;

    if (!showNewConversationModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <h3 className="font-SFPro font-semibold text-lg text-gray-900">Start new conversation</h3>
              <p className="font-inter text-sm text-gray-500 mt-1">Enter the recipient email to open a conversation</p>
            </div>
            <button
              type="button"
              onClick={this.closeNewConversationModal}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
          <form onSubmit={this.startConversation} className="px-5 py-5 flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
              <input
                type="email"
                value={to}
                onChange={(e) => this.setState({ to: e.target.value, error: null, success: null })}
                placeholder="customer@business.com"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end">
              <button
                type="submit"
                className="px-5 py-2.5 rounded-md bg-primary-blue text-white text-sm font-semibold hover:bg-blue-700"
              >
                Start
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  render() {
    const { selectedSession } = this.state;
    return (
      <>
        <div
          className="w-full h-screen p-4 flex gap-4 overflow-hidden"
          style={{
            background:
              "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
          }}
        >
          {this.renderSessionList()}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            {selectedSession ? (
              this.renderMessages()
            ) : (
              <div className="w-full h-full border border-gray-200 rounded-xl bg-white flex items-center justify-center p-4">
                <div className="flex flex-col items-center gap-4 px-8 text-center">
                  <Mail className="w-16 h-16 opacity-80 text-primary-blue" />
                  <h3 className="font-SFPro font-semibold text-xl text-gray-900">Start a conversation</h3>
                  <p className="font-inter text-sm text-gray-500 max-w-xs leading-relaxed">
                    Select a contact from the list to view or continue a conversation
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        {this.renderNewConversationModal()}
      </>
    );
  }
}

export default Email;
